// ── 매출 파일 업로드 훅 ───────────────────────────────────────────────────────
import { apiClient } from '../../../api/apiClient';

/**
 * 세금계산서 XML/홈택스 파일 업로드 + 카드승인 엑셀 업로드 핸들러 훅
 * @param {object} state   - useRevenue() 훅의 반환값
 * @param {Function} showToast - Toast 알림 함수
 * @returns {{ handleInvoiceFileSelect, handleFileUpload }}
 */
export function useRevenueUpload(state, showToast, apiBase = '/api/admin/revenue') {
  const {
    setInvoiceUploading, setUploading,
    loadInvoices, loadTaxBatches,
    loadCardApps, loadCardBatches,
    enqueueMigration,
  } = state;

  /**
   * 국세청 KEC 표준 세금계산서 XML 파싱
   * DOMParser 사용 — 서버 전송 없이 클라이언트에서 처리
   */
  function parseKecTaxInvoiceXml(xmlText) {
    const doc = new DOMParser().parseFromString(xmlText, 'application/xml');
    if (doc.querySelector('parsererror'))
      throw new Error('XML 파싱 오류: 올바른 세금계산서 파일인지 확인해 주세요.');

    // 네임스페이스 무관하게 태그명으로 텍스트 추출
    function g(parent, tag) {
      const els = parent.getElementsByTagNameNS('*', tag);
      return els.length ? els[0].textContent.trim() : '';
    }

    // 발행번호 (국세청 IssueID — 중복 체크 키)
    const taxDocEl = doc.getElementsByTagNameNS('*', 'TaxInvoiceDocument')[0];
    const issueId  = taxDocEl ? g(taxDocEl, 'IssueID') : '';

    // 발행일자 (YYYYMMDD → YYYY-MM-DD)
    const issueDateRaw = taxDocEl ? g(taxDocEl, 'IssueDateTime') : g(doc, 'IssueDateTime');
    const d = issueDateRaw.replace(/\D/g, '').slice(0, 8);
    const issueDate = d.length === 8 ? `${d.slice(0,4)}-${d.slice(4,6)}-${d.slice(6,8)}` : '';

    // 발행구분 (PurposeCode: 01=영수, 02=청구)
    const purposeCode = g(doc, 'PurposeCode');
    const issueType   = purposeCode === '02' ? '청구' : '영수';

    // 공급받는자 정보
    const invoiceeEl   = doc.getElementsByTagNameNS('*', 'InvoiceeParty')[0];
    const customerName = invoiceeEl ? g(invoiceeEl, 'NameText') : '';
    const bizNoRaw     = invoiceeEl ? g(invoiceeEl, 'ID')       : '';
    // 사업자번호 포맷: 10자리 → XXX-XX-XXXXX
    const bizNo = /^\d{10}$/.test(bizNoRaw)
      ? `${bizNoRaw.slice(0,3)}-${bizNoRaw.slice(3,5)}-${bizNoRaw.slice(5)}`
      : bizNoRaw;

    // 금액
    const summEl       = doc.getElementsByTagNameNS('*', 'SpecifiedMonetarySummation')[0];
    const supplyAmount = summEl ? g(summEl, 'ChargeTotalAmount') : '';
    const taxAmount    = summEl ? g(summEl, 'TaxTotalAmount')    : '';
    const totalAmount  = summEl ? g(summEl, 'GrandTotalAmount')  : '';

    return { issueId, issueDate, issueType, customerName, bizNo, supplyAmount, taxAmount, totalAmount };
  }

  /**
   * 세금계산서 파일 선택 통합 핸들러
   * - .xml:             KEC 국세청 표준 XML 파싱 후 개별 등록
   * - .xls/.xlsx/.csv/.txt: 홈택스 일괄 업로드
   */
  async function handleInvoiceFileSelect(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();

    if (ext === 'xml') {
      // ── XML 개별 등록 ──────────────────────────────────────────────────────
      const reader = new FileReader();
      reader.onload = async (ev) => {
        setInvoiceUploading(true);
        try {
          const parsed = parseKecTaxInvoiceXml(ev.target.result);
          if (!parsed.issueDate) {
            showToast('발행일자를 XML에서 읽을 수 없습니다.', 'error');
            return;
          }
          const r = await apiClient.post(`${apiBase}/tax-invoices`, {
            issue_id:      parsed.issueId,
            issue_date:    parsed.issueDate,
            issue_type:    parsed.issueType,
            customer_name: parsed.customerName,
            biz_no:        parsed.bizNo,
            supply_amount: parsed.supplyAmount,
            tax_amount:    parsed.taxAmount,
            total_amount:  parsed.totalAmount,
            file_name:     file.name, // 배치 이력 저장용
          });
          if (!r.data?.success) { showToast(r.data?.message || '등록 실패', 'error'); return; }
          showToast(
            `세금계산서 등록됐습니다. (${parsed.customerName || ''} ${Number(parsed.totalAmount).toLocaleString()}원)`,
            'success'
          );
          loadInvoices();
          loadTaxBatches();
          // 수정 세금계산서이면 이관 팝업 큐에 추가
          if (r.data.issueType === '수정') enqueueMigration(r.data);
        } catch (err) {
          showToast(err.message, 'error');
        } finally {
          setInvoiceUploading(false);
          e.target.value = '';
        }
      };
      reader.readAsText(file, 'UTF-8');

    } else if (['xls', 'xlsx', 'csv', 'txt'].includes(ext)) {
      // ── 홈택스 일괄 업로드 ────────────────────────────────────────────────
      setInvoiceUploading(true);
      try {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('originalFileName', file.name);
        const r = await apiClient.postForm(`${apiBase}/tax-invoices/upload-hometax`, fd);
        if (!r.data?.success) { showToast(r.data?.message || '업로드 실패', 'error'); return; }
        const { totalRows, newCount, skippedCount, modifiedInvoices } = r.data;
        showToast(`업로드 완료 — 전체 ${totalRows}건 (신규 ${newCount} / 중복스킵 ${skippedCount})`, 'success');
        loadInvoices();
        loadTaxBatches();
        // 수정 세금계산서가 포함되어 있으면 이관 큐에 추가
        if (modifiedInvoices?.length) enqueueMigration(modifiedInvoices);
      } catch (err) {
        showToast(err.message || '업로드 중 오류가 발생했습니다.', 'error');
      } finally {
        setInvoiceUploading(false);
        e.target.value = '';
      }

    } else {
      showToast('xml, xls, xlsx, csv, txt 파일만 업로드 가능합니다.', 'error');
      e.target.value = '';
    }
  }

  /** 카드승인 엑셀/CSV 업로드 */
  async function handleFileUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'xls', 'csv'].includes(ext)) {
      showToast('xlsx, xls, csv 파일만 업로드 가능합니다.', 'error');
      e.target.value = '';
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      // 한글 파일명 깨짐 방지: 파일명을 별도 텍스트 필드로 명시 전송
      fd.append('originalFileName', file.name);
      const r = await apiClient.postForm(`${apiBase}/card-approvals/upload`, fd);
      if (!r.data?.success) { showToast(r.data?.message || '업로드 실패', 'error'); return; }
      const { totalRows, newCount, updatedCount, skippedCount } = r.data;
      showToast(`업로드 완료 — 전체 ${totalRows}건 (신규 ${newCount} / 갱신 ${updatedCount} / 스킵 ${skippedCount})`, 'success');
      loadCardApps();
      loadCardBatches();
    } catch (err) {
      showToast(err?.response?.data?.message || '업로드 중 오류가 발생했습니다.', 'error');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  }

  return { handleInvoiceFileSelect, handleFileUpload };
}
